import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import AovModal from '../AovModal';
import CartConfirmationModal from '../../../components/CartConfirmationModal';
import DisabledAddToCart from '../../../components/common/DisabledAddToCart';
import messages from './messages';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import sessionStorageService from '../../../services/sessionStorage';
import useSlaService from '../../../hooks/useSlaService';
import { getVariantsWithPercentDiscount } from '../../../utils/variants';
import {
  addLineItems,
  addPromoCode,
  removePromoCode,
} from '../../../slices/cart';
import {
  getBundlePdpAovItemsToRender,
  getBundlePdpAovTargetProductVariant,
} from '../../../utils';
import {
  NEWDAY_TPM_PROMO,
  ABANDONED_CART_PROMO_CODES_REGULAR_EXPRESSION,
  CONFLICTING_PROMO_TOASTER_TYPE_TO_SHOW_SESSION_STORAGE_KEY,
  CONFLICTING_PROMOS_TOAST_TYPE_10OFF_REMOVED,
  ITEM_LIST_IDS_TEALIUM,
  STORE_LOCATOR_URL,
  STORE_LOCATOR_CTA_FEATURE_NAME,
} from '../../../utils/common/constants';
import useTealium from '../../../hooks/useTealium/useTealium';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import { setPDPEstimatedTotal } from '../../../slices/pdp';
import AddToCartSimplifiedView from '../../../components/pdpV2/AddToCartSimplifiedView';
import TryInStoreButton from '../../../components/TryInStoreButton';
import useGetUserSessionInfo from '../../../hooks/queries/useGetUserSessionInfo';
import useGetStoresNearUser from '../../../hooks/queries/useGetStoresNearUser';
import useGetStoresWithProductAvailableNearUser from '../../../hooks/queries/useGetStoresWithProductAvailableNearUser';

const AddToCartPDPContainer = () => {
  const { publish } = drupalPubSubService;
  const {
    aovs,
    product: productFromDrupalSettings,
    featureToggle,
    rioMappingTable,
  } = useDrupalSettings();

  const {
    selectedSKU,
    selectedFirstProductVariant,
    selectedWarrantyPlan,
    selectedSecondSKU,
    selectedQty,
    selectedBedFrameAOVSKUs,
    selectedComplementaryAOVSKU,
    selectedBundlePdpAOVSKUs,
  } = useSelector((state) => state.pdp);

  const userSessionInfo = useGetUserSessionInfo();

  const userZIPCode = userSessionInfo?.data?.postalCode;

  useGetStoresNearUser({ ZIPCode: userZIPCode });

  const rioProductId = rioMappingTable?.[productFromDrupalSettings?.key];

  useGetStoresWithProductAvailableNearUser({
    ZIPCode: userZIPCode,
    rioProductId,
  });

  const abandonedCartDiscountsApplied = useSelector((state) => {
    const discounts = state.cart.lineItems
      ?.filter((lineItem) => lineItem.custom?.fields?.discounts_json)
      .flatMap((lineItem) =>
        JSON.parse(lineItem.custom?.fields?.discounts_json).filter(
          (discount) => discount.method === 'coupon',
        ),
      )
      .map((discountJson) => discountJson.coupon_code)
      .filter((discount) => discount !== NEWDAY_TPM_PROMO)
      .filter((discount) =>
        ABANDONED_CART_PROMO_CODES_REGULAR_EXPRESSION.test(discount),
      );

    return [...new Set(discounts)];
  });

  const { formatMessage } = useIntl();

  const selectedSkuAov = aovs?.find((aov) => aov.targetSku === selectedSKU);

  const selectedWarrantyPlanASJSON = JSON.stringify(selectedWarrantyPlan);

  const getMatchingVariant = () =>
    productFromDrupalSettings.variants.find((sku) => sku.sku === selectedSKU);

  const isShowAovFreeGiftAdvertisement =
    !!selectedSkuAov?.gifts?.options?.length;

  // I toString() the array to avoid a infinite loop on jest tests.
  // this variable is required by the linter in order to hide a warning message.
  const selectedBundlePdpAOVSKUsString = selectedBundlePdpAOVSKUs.toString();
  const selectedBedFrameAOVSKUsString = selectedBedFrameAOVSKUs.toString();
  const slaInput = useMemo(
    () =>
      [
        selectedSKU,
        ...selectedBedFrameAOVSKUs,
        selectedComplementaryAOVSKU,
        ...selectedBundlePdpAOVSKUs,
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedSKU,
      selectedBedFrameAOVSKUsString,
      selectedComplementaryAOVSKU,
      selectedBundlePdpAOVSKUsString, // this dependency generates a linter error that it's solved by disabling the linter rule.
    ],
  );

  const isStoreLocatorFeatureEnabled = featureToggle?.includes(
    STORE_LOCATOR_CTA_FEATURE_NAME,
  );

  const getRioStoresNearUserFromSessionStorage = () => {
    try {
      return JSON.parse(
        sessionStorageService.getItem({
          itemName: 'storesWithProductAvailableNearUser',
        }) ?? '{}',
      );
    } catch {
      return {};
    }
  };

  const rioStoresNearUserInfoFromSessionStorage =
    getRioStoresNearUserFromSessionStorage();

  const isAPurpleStoreNearUser = Array.isArray(
    rioStoresNearUserInfoFromSessionStorage?.data,
  )
    ? rioStoresNearUserInfoFromSessionStorage?.data?.some(
        (store) =>
          store['Store Type'] && store['Store Type'].includes('Purple Store'),
      )
    : false;

  const hasToShowTryInStoreCTA =
    isStoreLocatorFeatureEnabled && isAPurpleStoreNearUser;

  // Call SLA Service, so PDP SLA Messaging can be updated in Redux store.
  // Needed for Drupal Dynamic SLA in Worry-Free Guarantees component.
  useSlaService(
    slaInput,
    selectedBedFrameAOVSKUs.length > 0 || !!selectedComplementaryAOVSKU,
  );
  const { isLoading, addLineItemsError } = useSelector((state) => ({
    addLineItemsError: state.cart.addLineItemsError,
    isLoading:
      state.cart?.status === 'loading' || state.cart?.status === 'idle',
  }));

  const dispatch = useDispatch();

  const [selectedComplementaryAOVVariant, setSelectedComplementaryAOVVariant] =
    useState();
  const [isAovModalOpen, setAovModalOpen] = useState(false);
  const [isCartConfirmationModalOpen, setIsCartConfirmationModalOpen] =
    useState(false);
  const [selectedBundlePdpAOVVariants, setSelectedBundlePdpAOVVariants] =
    useState([]);
  const [
    selectedBundlePdpAOVTargetProduct,
    setSelecedBundlePdpAOVTargetProduct,
  ] = useState({});

  const [total, setTotal] = useState({});
  const [totalDiscount, setTotalDiscount] = useState({});
  const [handleAddToCartClicked, setHandleAddToCartClicked] = useState(false);

  const toggleAovModal = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      setAovModalOpen((prevState) => !prevState);
    },
    [setAovModalOpen],
  );

  const toggleCartConfirmationModalModal = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      setIsCartConfirmationModalOpen((prevState) => !prevState);
    },
    [setIsCartConfirmationModalOpen],
  );

  useTealium({ handleAddToCartClicked });

  const handleTryInStoreClick = () => {
    const url = new URL(STORE_LOCATOR_URL, window.location.origin);
    url.search = new URLSearchParams({
      q: userZIPCode,
      check_list: rioProductId,
    }).toString();
    window.location.href = url.toString();
  };

  const handleAddToCart = useCallback(async () => {
    const associatedWarrantyItems = selectedWarrantyPlan?.filter(
      (warrantyItem) => warrantyItem.product === selectedSKU,
    );
    const bedFrameWarrantyItems = selectedWarrantyPlan?.filter((warrantyItem) =>
      selectedBedFrameAOVSKUs.includes(warrantyItem.product),
    );

    let lineItems = [
      {
        sku: selectedSKU,
        quantity:
          selectedSecondSKU && selectedSKU === selectedSecondSKU
            ? 2
            : selectedQty,
        warrantyPlanId: associatedWarrantyItems?.[0]?.planId,
        warrantyPrice: associatedWarrantyItems?.[0]?.price,
        item_list_id: ITEM_LIST_IDS_TEALIUM.productDetailPage,
      },
    ];
    if (selectedSecondSKU && selectedSecondSKU !== selectedSKU) {
      lineItems = [
        ...lineItems,
        {
          sku: selectedSecondSKU,
          item_list_id: ITEM_LIST_IDS_TEALIUM.productDetailPage,
        },
      ];
    }
    if (selectedBedFrameAOVSKUs.length > 0) {
      lineItems = [
        ...lineItems,
        ...selectedBedFrameAOVSKUs.map((sku) => ({
          sku,
          warrantyPlanId: bedFrameWarrantyItems?.[0]?.planId,
          warrantyPrice: bedFrameWarrantyItems?.[0]?.price,
          item_list_id: ITEM_LIST_IDS_TEALIUM.productDetailPageAOV,
        })),
      ];
    }

    if (selectedComplementaryAOVSKU) {
      lineItems = [
        ...lineItems,
        {
          sku: selectedComplementaryAOVSKU,
          item_list_id: ITEM_LIST_IDS_TEALIUM.minicartAovDrivers,
        },
      ];
    }

    if (selectedBundlePdpAOVSKUs?.length > 0) {
      const selectedBundlePdpAOVSkusWithQuantity =
        selectedBundlePdpAOVSKUs.reduce(
          (acc, cur) =>
            acc[cur] ? { ...acc, [cur]: acc[cur] + 1 } : { ...acc, [cur]: 1 },
          {},
        );
      const newLineItems = Object.entries(
        selectedBundlePdpAOVSkusWithQuantity,
      ).map((selectedBundleSkuWithQuantity) => ({
        sku: selectedBundleSkuWithQuantity[0],
        quantity: selectedBundleSkuWithQuantity[1],
        item_list_id: ITEM_LIST_IDS_TEALIUM.minicartAovDrivers,
      }));
      lineItems = [...lineItems, ...newLineItems];
    }
    const response = await dispatch(addLineItems({ lineItems }));

    if (!response.error) {
      const matchingVariant = getMatchingVariant();
      if (matchingVariant?.isProductTest) {
        if (abandonedCartDiscountsApplied?.length) {
          // Using classic for because we need to wait until each promise is resolved (to get the new version of the cart)
          // eslint-disable-next-line no-restricted-syntax
          for (const abandonedCartDiscount of abandonedCartDiscountsApplied) {
            // eslint-disable-next-line no-await-in-loop
            await dispatch(removePromoCode(abandonedCartDiscount));
          }
          sessionStorage.setItem(
            CONFLICTING_PROMO_TOASTER_TYPE_TO_SHOW_SESSION_STORAGE_KEY,
            CONFLICTING_PROMOS_TOAST_TYPE_10OFF_REMOVED,
          );
        }
        dispatch(addPromoCode(NEWDAY_TPM_PROMO));
        localStorage.setItem('product_upgrade_test', selectedSKU);
      }
      if (
        selectedSkuAov &&
        ((selectedSkuAov.bundles &&
          !selectedSkuAov.bundles.showOnPdp &&
          !selectedSkuAov.bundles.showOnPdpV2) ||
          selectedSkuAov.buyMore ||
          selectedSkuAov.gifts ||
          selectedSkuAov.flatRate ||
          selectedSkuAov.bundleGifts)
      ) {
        dispatch(
          setPDPEstimatedTotal({
            totalPrice: {
              centAmount: 0,
            },
          }),
        );
        toggleAovModal();
        setHandleAddToCartClicked(true);
      } else {
        toggleCartConfirmationModalModal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSKU,
    selectedSecondSKU,
    selectedQty,
    selectedWarrantyPlan,
    selectedBedFrameAOVSKUsString,
    selectedComplementaryAOVSKU,
    selectedSkuAov,
    dispatch,
    productFromDrupalSettings?.variants,
    toggleAovModal,
    toggleCartConfirmationModalModal,
    selectedBundlePdpAOVSKUsString, // this dependency generates a linter error that it's solved by disabling the linter rule.
  ]);

  const selectedBedFrameAOVVariants = useMemo(() => {
    if (!selectedBedFrameAOVSKUs.length || aovs.length <= 0) {
      return [];
    }

    const bedFrameAOVForSelectedSKU = aovs.find(
      ({ targetSku }) => targetSku === selectedSKU,
    )?.frames;

    const bedFrameAOVVariantsForSelectedSKU =
      bedFrameAOVForSelectedSKU?.options
        ?.map((option) =>
          option?.variants?.map((variant) => ({
            ...variant,
            productKey: option?.productKey,
          })),
        )
        .flat() || [];

    const bedFrameAOVAltProductsVariantsForSelectedSKU =
      bedFrameAOVForSelectedSKU?.options
        ?.map((option) =>
          option?.alternativeProduct?.product?.variants?.map((variant) => ({
            ...variant,
            productKey: option?.alternativeProduct?.product?.productKey,
          })),
        )
        .filter(Boolean)
        .flat() || [];

    return [
      ...bedFrameAOVVariantsForSelectedSKU,
      ...bedFrameAOVAltProductsVariantsForSelectedSKU,
    ].filter(({ sku }) => selectedBedFrameAOVSKUs.includes(sku));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aovs, selectedBedFrameAOVSKUsString, selectedSKU]);

  useEffect(() => {
    if (selectedComplementaryAOVSKU) {
      const complementaryAOVForSelectedSKU = aovs?.find(
        (aov) => aov.targetSku === selectedSKU,
      )?.complementary;

      const complementaryAOVVariantsForSelectedSKU =
        complementaryAOVForSelectedSKU?.options
          ?.map((option) => option?.variants)
          .flat();
      const selectedComplementaryVariant =
        complementaryAOVVariantsForSelectedSKU?.find(
          (variant) => selectedComplementaryAOVSKU === variant.sku,
        );

      if (selectedComplementaryVariant) {
        setSelectedComplementaryAOVVariant(selectedComplementaryVariant);
      }
    } else {
      setSelectedComplementaryAOVVariant(undefined);
    }
  }, [selectedComplementaryAOVSKU, selectedSKU, aovs]);

  useEffect(() => {
    const bundleAOVsV1 = aovs?.find(
      ({ targetSku, bundles }) =>
        targetSku === selectedSKU && bundles?.showOnPdp,
    );

    if (bundleAOVsV1) {
      const variants = new Set(); // I use a Set to avoid duplicate entries.
      const { bundles } = bundleAOVsV1;
      const { items } = bundles;
      const itemsToTakeIntoAccount = getBundlePdpAovItemsToRender(
        items,
        selectedSKU,
      );
      const bundleTargetProduct = getBundlePdpAovTargetProductVariant(
        items,
        selectedSKU,
      );
      setSelecedBundlePdpAOVTargetProduct(bundleTargetProduct);
      itemsToTakeIntoAccount.forEach(({ options }) => {
        options.forEach((option) =>
          option.variants.forEach((variant) => {
            if (selectedBundlePdpAOVSKUs.includes(variant.sku)) {
              variants.add(variant);
            }
          }),
        );
      });
      setSelectedBundlePdpAOVVariants(Array.from(variants));
    } else {
      const bundleAOVsV2 = aovs?.find(
        ({ targetSku, bundles }) =>
          targetSku === selectedSKU && bundles?.showOnPdpV2,
      );
      const bundleTargetProduct = getBundlePdpAovTargetProductVariant(
        bundleAOVsV2?.bundles?.items,
        selectedSKU,
      );
      setSelecedBundlePdpAOVTargetProduct(bundleTargetProduct);
      if (bundleAOVsV2) {
        const allBundleItemsVariants = bundleAOVsV2?.bundles?.items?.flatMap(
          (bundleItem) =>
            bundleItem.options?.flatMap(
              (bundleItemOption) => bundleItemOption.variants,
            ),
        );
        const bundlePdpAOVSelectedVariants = selectedBundlePdpAOVSKUs
          .map((selectedBundleAOVSku) =>
            allBundleItemsVariants.find(
              (variant) => variant.sku === selectedBundleAOVSku,
            ),
          )
          .filter(Boolean);

        setSelectedBundlePdpAOVVariants(bundlePdpAOVSelectedVariants);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aovs,
    selectedBundlePdpAOVSKUsString, // this dependency generates a linter error that it's solved by disabling the linter rule.
    selectedSKU,
  ]);

  useEffect(
    () => {
      if (!selectedFirstProductVariant) {
        return;
      }

      const selectedWarrantyPlanParsed = JSON.parse(selectedWarrantyPlanASJSON);

      const discountPercentage =
        selectedSecondSKU &&
        productFromDrupalSettings?.secondItemDiscount?.percentage
          ? productFromDrupalSettings?.secondItemDiscount?.percentage
          : 0;

      const discountedFirstVariant =
        discountPercentage > 0
          ? getVariantsWithPercentDiscount(
              [selectedFirstProductVariant],
              discountPercentage,
            )[0]
          : selectedFirstProductVariant;

      let centAmount;
      let centAmountDiscount;

      if (selectedBundlePdpAOVSKUs.length > 0) {
        centAmount =
          selectedBundlePdpAOVTargetProduct.price.centAmount * selectedQty;
        centAmountDiscount =
          selectedBundlePdpAOVTargetProduct.discount.centAmount * selectedQty;
      } else {
        centAmount = discountedFirstVariant.price.centAmount * selectedQty;
        centAmountDiscount =
          (discountedFirstVariant.discount?.centAmount ?? 0) * selectedQty;
      }

      if (selectedBedFrameAOVVariants.length > 0) {
        selectedBedFrameAOVVariants.forEach((variant) => {
          centAmount += variant.price.centAmount;

          centAmountDiscount += variant.discount.centAmount;
        });
      }

      if (selectedComplementaryAOVVariant) {
        centAmount += selectedComplementaryAOVVariant.price.centAmount;

        centAmountDiscount +=
          selectedComplementaryAOVVariant.discount.centAmount;
      }

      if (selectedSecondSKU) {
        const variant = productFromDrupalSettings.variants.find(
          (v) => v.sku === selectedSecondSKU,
        );
        if (variant) {
          const discountedSecondVariant = getVariantsWithPercentDiscount(
            [variant],
            discountPercentage,
          )[0];

          centAmount += discountedSecondVariant.price.centAmount;
          centAmountDiscount += discountedSecondVariant.discount.centAmount;
        }
      }

      const totalPrice = selectedWarrantyPlanParsed?.reduce(
        (prev, cur) => prev + cur.price,
        0,
      );

      if (totalPrice) {
        centAmount += totalPrice;
      }

      selectedBundlePdpAOVVariants.forEach((bundlePdpAOVVariant) => {
        centAmount += bundlePdpAOVVariant.price.centAmount;
        centAmountDiscount += bundlePdpAOVVariant.discount.centAmount;
      });

      const newTotal = {
        ...selectedFirstProductVariant.price,
        centAmount,
      };

      setTotal(newTotal);

      publish({
        subscription: 'onPDPTotalChange',
        parameters: { newTotal },
      });

      setTotalDiscount({
        ...selectedFirstProductVariant.price,
        centAmount: centAmountDiscount,
      });
      dispatch(
        setPDPEstimatedTotal({
          totalPrice: {
            ...selectedFirstProductVariant.price,
            centAmount,
          },
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productFromDrupalSettings,
      selectedBedFrameAOVVariants,
      selectedComplementaryAOVVariant,
      selectedQty,
      // used instead of the whole selectedFirstProductVariant because it causes infinite rendering on jest tests
      selectedFirstProductVariant?.sku, // this dependency generates a linter error that it's solved by disabling the linter rule.
      selectedSecondSKU,
      selectedBundlePdpAOVVariants,
      selectedBundlePdpAOVTargetProduct,
      setTotal,
      setTotalDiscount,
      selectedWarrantyPlanASJSON,
      selectedBedFrameAOVSKUsString,
    ],
  );

  if (productFromDrupalSettings.disabledATC) {
    return (
      <DisabledAddToCart
        addToCartButtonTitle={formatMessage(messages.disabledATCAddToCart)}
      />
    );
  }

  return (
    <>
      <AddToCartSimplifiedView
        addToCartButtonTitle={formatMessage(
          selectedSKU && selectedBedFrameAOVVariants.length > 0
            ? messages.addToCartMultipleItems
            : messages.addToCartSingleItem,
        )}
        selectedSku={selectedSKU}
        hasAddLineItemsError={addLineItemsError}
        handleAddToCart={handleAddToCart}
        isAddToCartButtonLoading={isLoading}
        productType={productFromDrupalSettings.productType}
        showTotals={!!selectedFirstProductVariant}
        showAovFreeGiftAdvertisement={isShowAovFreeGiftAdvertisement}
        total={total}
        totalDiscount={totalDiscount}
        productTitle={productFromDrupalSettings?.name}
      >
        {hasToShowTryInStoreCTA && (
          <TryInStoreButton onClick={handleTryInStoreClick} />
        )}
      </AddToCartSimplifiedView>
      {isAovModalOpen && (
        <AovModal
          data-testid='aov-modal'
          onCloseModal={toggleAovModal}
          aovs={aovs}
        />
      )}
      {isCartConfirmationModalOpen && (
        <CartConfirmationModal
          data-testid='cart-confirmation-modal'
          onCloseModal={toggleCartConfirmationModalModal}
        />
      )}
    </>
  );
};

export default AddToCartPDPContainer;
